import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import FinanceNote from "@/components/FinanceNote";
import StageGuide from "@/components/funding/StageGuide";
import FunderPathway from "@/components/funding/FunderPathway";
import FundingForm, { initialFundingEnquiry } from "@/components/funding/FundingForm";
import {
  fundingHero,
  fundingNeeds,
  fundingPathways,
  fundingTogether,
  projectStages,
  funderPathway,
  riskSection,
  ourRole,
  fundingFooterNote,
  fundingSupport,
  readinessLink,
  beyondProjects,
} from "@/content/funding";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Funding() {
  // Enquiry state lives at page level so selections made in any section
  // (stage guide, funder interests, pathway panels) are preserved and
  // carried into the form.
  const [enquiry, setEnquiry] = useState(initialFundingEnquiry);
  const [selectedStage, setSelectedStage] = useState(null);
  const [funderInterests, setFunderInterests] = useState([]);
  const [funderContributions, setFunderContributions] = useState([]);

  const updateField = (field, value) => setEnquiry((s) => ({ ...s, [field]: value }));
  const toggleListItem = (field, item) =>
    setEnquiry((s) => ({
      ...s,
      [field]: s[field].includes(item)
        ? s[field].filter((i) => i !== item)
        : [...s[field], item],
    }));

  const toggleFrom = (list, setter) => (item) =>
    setter(list.includes(item) ? list.filter((i) => i !== item) : [...list, item]);

  const openProjectEnquiry = (stageId) => {
    const stage = projectStages.stages.find((s) => s.id === stageId);
    setEnquiry((s) => ({
      ...s,
      kind: "project",
      project_stage: stageId,
      topic: stage ? stage.button.replace(/^Discuss /, "").replace(/^./, (c) => c.toUpperCase()) : s.topic,
    }));
    scrollTo("funding-enquiry");
  };

  const openFunderEnquiry = () => {
    setEnquiry((s) => ({
      ...s,
      kind: "funder",
      areas_of_interest: funderInterests,
      contribution_types: funderContributions,
    }));
    scrollTo("funding-enquiry");
  };

  const openTopicEnquiry = (topic) => {
    setEnquiry((s) => ({ ...s, topic }));
    scrollTo("funding-enquiry");
  };

  return (
    <>
      <Seo
        title="Community Project Funding | Bioregional Ventures"
        description="Funding pathways for community land acquisition, community farms and ecological restoration — philanthropy, grants for community farms and patient finance, in plain language."
      />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-20">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark mb-4" data-testid="funding-eyebrow">
            {fundingHero.eyebrow}
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight text-charcoal max-w-3xl" data-testid="funding-headline">
            {fundingHero.headline}
          </h1>
          {fundingHero.body.map((p) => (
            <p key={p.slice(0, 24)} className="mt-6 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
              {p}
            </p>
          ))}
          <div className="mt-10 flex flex-wrap gap-4">
            {fundingHero.primaryCtas.map((cta, i) => (
              <button
                key={cta.target}
                type="button"
                onClick={() => scrollTo(cta.target)}
                data-testid={`funding-hero-cta-${i + 1}`}
                className={
                  i === 0
                    ? "bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
                    : "border border-eucalyptus text-eucalyptus px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus hover:text-cream transition-colors duration-300"
                }
              >
                {cta.label}
              </button>
            ))}
          </div>
          <nav aria-label="Funding topics" className="mt-10 flex flex-wrap gap-x-6 gap-y-3" data-testid="funding-links-row">
            {fundingHero.links.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`funding-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
                className="text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </Reveal>
      </section>

      {/* Funding support introduction */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-16 md:pb-20">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="funding-support-heading">
            {fundingSupport.heading}
          </h2>
          {fundingSupport.body.map((p) => (
            <p key={p.slice(0, 24)} className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90">
              {p}
            </p>
          ))}
        </Reveal>

        <Reveal className="mt-10">
          <p className="text-base md:text-lg leading-relaxed text-charcoal/90" data-testid="readiness-link-para">
            {readinessLink.text}{" "}
            <Link
              to={readinessLink.href}
              data-testid="readiness-link"
              className="text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
            >
              {readinessLink.linkText}
            </Link>
          </p>
        </Reveal>

        <Reveal className="mt-14 border border-charcoal/15 bg-sage/50 p-7 md:p-9" >
          <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal" data-testid="beyond-projects-heading">
            {beyondProjects.heading}
          </h3>
          <p className="mt-4 text-base leading-relaxed text-charcoal/90" data-testid="beyond-projects-ds-para">
            {beyondProjects.dsLink.before}
            <Link
              to={beyondProjects.dsLink.href}
              data-testid="beyond-projects-ds-link"
              className="text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
            >
              {beyondProjects.dsLink.linkText}
            </Link>
            {beyondProjects.dsLink.after}
          </p>
          <p className="mt-4 text-base leading-relaxed text-charcoal/90" data-testid="beyond-projects-closing">
            {beyondProjects.closing}
          </p>
        </Reveal>
      </section>

      {/* What needs funding */}
      <section className="bg-sage/60 border-y border-charcoal/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="funding-needs-heading">
              {fundingNeeds.heading}
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {fundingNeeds.cards.map((card, i) => (
              <Reveal key={card.title} delay={i * 0.06} className="h-full">
                <div className="h-full border border-charcoal/15 bg-cream p-6" data-testid={`funding-need-card-${i + 1}`}>
                  <h3 className="font-serif text-lg text-charcoal leading-snug">{card.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/85">{card.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl" data-testid="funding-needs-closing">
              {fundingNeeds.closing}
            </p>
            <p className="mt-8 text-sm text-charcoal/60 max-w-2xl" data-testid="funding-needs-note">
              {fundingNeeds.note}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Three funding pathways */}
      <section id="funding-pathways" className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 scroll-mt-24">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="pathways-heading">
            Three funding pathways
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {fundingPathways.map((pathway, i) => (
            <Reveal key={pathway.id} delay={i * 0.06} className="h-full">
              <article
                className="h-full border border-charcoal/15 bg-cream p-7 md:p-8 flex flex-col"
                data-testid={`pathway-panel-${pathway.id}`}
              >
                <h3 className="font-serif text-2xl text-charcoal">{pathway.title}</h3>
                <p className="mt-2 font-serif italic text-ochre-dark">{pathway.standfirst}</p>
                <p className="mt-4 text-sm leading-relaxed text-charcoal/85">{pathway.body}</p>
                {pathway.contributors && (
                  <p className="mt-4 text-sm leading-relaxed text-charcoal/85">
                    <span className="font-medium text-charcoal">{pathway.contributorsLabel}: </span>
                    {pathway.contributors}
                  </p>
                )}
                <p className="mt-5 text-xs uppercase tracking-[0.15em] text-ochre-dark">
                  Questions to resolve
                </p>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-charcoal/85 list-disc pl-5 flex-1">
                  {pathway.questions.map((q) => (
                    <li key={q}>{q}</li>
                  ))}
                </ul>
                {pathway.note && (
                  <p className="mt-5 text-xs leading-relaxed text-charcoal/60">{pathway.note}</p>
                )}
                <button
                  type="button"
                  data-testid={`pathway-button-${pathway.id}`}
                  onClick={() => openTopicEnquiry(pathway.topic)}
                  className="mt-6 self-start border border-eucalyptus text-eucalyptus px-5 py-2.5 rounded-sm text-sm hover:bg-eucalyptus hover:text-cream transition-colors duration-300"
                >
                  {pathway.button}
                </button>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How funding sources can work together */}
      <section className="bg-eucalyptus text-cream">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug max-w-2xl" data-testid="funding-together-heading">
              {fundingTogether.heading}
            </h2>
            {fundingTogether.body.map((p) => (
              <p key={p.slice(0, 24)} className="mt-6 text-base md:text-lg leading-relaxed text-cream/85 max-w-2xl">
                {p}
              </p>
            ))}
          </Reveal>
          <Reveal className="mt-10 overflow-x-auto">
            <table className="w-full text-left text-sm md:text-base border border-cream/25" data-testid="funding-together-table">
              <thead>
                <tr className="border-b border-cream/25">
                  {fundingTogether.table.columns.map((col) => (
                    <th key={col} scope="col" className="p-4 md:p-5 font-serif text-lg font-medium">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fundingTogether.table.rows.map((row, i) => (
                  <tr key={row[0]} className={i % 2 === 0 ? "bg-cream/5" : ""}>
                    <td className="p-4 md:p-5 align-top text-cream/90">{row[0]}</td>
                    <td className="p-4 md:p-5 align-top text-cream/80">{row[1]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
          <Reveal>
            <p className="mt-6 text-sm text-cream/70 border-l-2 border-ochre pl-4 max-w-3xl" data-testid="funding-together-disclaimer">
              {fundingTogether.table.disclaimer}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Project pathway */}
      <section id="project-pathway" className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 scroll-mt-24">
        <StageGuide
          content={projectStages}
          selectedStage={selectedStage}
          onSelectStage={setSelectedStage}
          onEnquire={openProjectEnquiry}
        />
      </section>

      {/* Funder pathway */}
      <section id="funder-pathway" className="bg-sage/60 border-y border-charcoal/10 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <FunderPathway
            content={funderPathway}
            interests={funderInterests}
            contributions={funderContributions}
            onToggleInterest={toggleFrom(funderInterests, setFunderInterests)}
            onToggleContribution={toggleFrom(funderContributions, setFunderContributions)}
            onEnquire={openFunderEnquiry}
          />
        </div>
      </section>

      {/* Clear responsibilities */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="risk-heading">
            {riskSection.heading}
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90" data-testid="risk-intro">
            {riskSection.introBefore}
            <Link
              to={riskSection.introHref}
              data-testid="risk-questions-link"
              className="text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
            >
              {riskSection.introLink}
            </Link>
            {riskSection.introAfter}
          </p>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90" data-testid="risk-closing">
            {riskSection.closing}
          </p>
        </Reveal>
      </section>

      {/* Funding readiness link */}
      <section className="bg-sage/60 border-y border-charcoal/10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-16">
          <Reveal>
            <p className="text-base md:text-lg leading-relaxed text-charcoal/90" data-testid="our-role-link-para">
              {ourRole.text}
              <Link
                to={ourRole.href}
                data-testid="our-role-link"
                className="text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
              >
                {ourRole.linkText}
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Enquiry form */}
      <section id="funding-enquiry" className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 scroll-mt-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          <Reveal className="md:col-span-4">
            <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark mb-4">Enquire</p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="funding-form-heading">
              Start the conversation
            </h2>
            <p className="mt-6 text-base leading-relaxed text-charcoal/90">
              Choose the enquiry type that fits, and tell us a little about the project or the
              support you are considering. Please don't include sensitive financial documents at
              this stage — a short outline is plenty.
            </p>
          </Reveal>
          <Reveal className="md:col-span-8" delay={0.08}>
            <FundingForm enquiry={enquiry} updateField={updateField} toggleListItem={toggleListItem} />
          </Reveal>
        </div>
      </section>

      {/* Contextual funding note */}
      <section className="border-t border-charcoal/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-16">
          <Reveal>
            <p className="text-sm text-charcoal/70 italic" data-testid="funding-footer-note">
              {fundingFooterNote}
            </p>
            <p className="mt-4 text-sm text-charcoal/80">
              Looking for background reading on the commons, community ownership and local
              economies? Browse{" "}
              <Link
                to="/resources"
                data-testid="funding-resources-link"
                className="text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
              >
                the reading list in Resources
              </Link>
              .
            </p>
            <FinanceNote />
          </Reveal>
        </div>
      </section>
    </>
  );
}
